import { useState, useMemo } from "react";
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString } from "libphonenumber-js";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

let regionNames;
try {
  regionNames = new Intl.DisplayNames(["en"], { type: "region" });
} catch {
  regionNames = null;
}

const COUNTRIES = getCountries()
  .map((iso) => {
    let name = iso;
    try {
      name = regionNames ? regionNames.of(iso) : iso;
    } catch {
      name = iso;
    }
    return { iso, calling: getCountryCallingCode(iso), name };
  })
  .sort((a, b) => a.name.localeCompare(b.name));

// PhoneInput emits an E.164 string (e.g. +919876543210) via onChange.
// `value` pre-fills the inputs with an existing E.164 number.
export default function PhoneInput({ onChange, value = "", defaultCountry = "IN", testidPrefix = "phone" }) {
  const initial = useMemo(() => {
    if (!value) return { country: defaultCountry, national: "" };
    try {
      const p = parsePhoneNumberFromString(value);
      if (p && p.country) return { country: p.country, national: p.nationalNumber };
    } catch { /* fall through */ }
    return { country: defaultCountry, national: "" };
  }, []); // mount-only hydration
  const [country, setCountry] = useState(initial.country);
  const [national, setNational] = useState(initial.national);

  const calling = useMemo(() => {
    try {
      return getCountryCallingCode(country);
    } catch {
      return "";
    }
  }, [country]);

  const emit = (c, n) => {
    const digits = (n || "").replace(/\D/g, "");
    let code = "";
    try {
      code = getCountryCallingCode(c);
    } catch {
      code = "";
    }
    onChange(digits ? `+${code}${digits}` : "");
  };

  return (
    <div className="flex gap-2">
      <Select
        value={country}
        onValueChange={(v) => {
          setCountry(v);
          emit(v, national);
        }}
      >
        <SelectTrigger className="w-[130px] shrink-0 font-mono-num" data-testid={`${testidPrefix}-country-select`}>
          <SelectValue>{country} +{calling}</SelectValue>
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {COUNTRIES.map((c) => (
            <SelectItem key={c.iso} value={c.iso} data-testid={`${testidPrefix}-country-${c.iso}`}>
              {c.name} (+{c.calling})
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        className="font-mono-num"
        inputMode="numeric"
        placeholder="98765 43210"
        value={national}
        data-testid={`${testidPrefix}-number-input`}
        onChange={(e) => {
          const v = e.target.value.replace(/[^\d\s-]/g, "");
          setNational(v);
          emit(country, v);
        }}
      />
    </div>
  );
}
